import { useTranslation } from 'react-i18next';

import { CreateIcon, DeliverIcon, PlanIcon, ScaleIcon } from '@/shared/ui/GeneralIcons';
import type { ToolCategoryId } from '@/shared/constants/category-colors';

import { LEARN_CARD_CONFIG } from '../../model';
import type { BrowseByCategorySectionProps } from './BrowseByCategorySection.types';

const CATEGORY_ICONS: Record<ToolCategoryId, React.ComponentType<{ className?: string }>> = {
  plan: PlanIcon,
  create: CreateIcon,
  scale: ScaleIcon,
  deliver: DeliverIcon,
};

/**
 * Reading order of the 2x2 grid in the design, which pairs the two halves of a
 * campaign down each column rather than following the pipeline across.
 */
const CARD_ORDER: readonly ToolCategoryId[] = ['plan', 'scale', 'create', 'deliver'];

/**
 * The way in for someone who does not yet know which tool they want: four
 * category doors beside the question that sends them through one.
 */
export function BrowseByCategorySection({
  categoryLabels,
  onCategoryClick,
}: BrowseByCategorySectionProps) {
  const { t } = useTranslation('tools');

  return (
    <section
      data-testid="browse-by-category"
      className="content-block grid items-center gap-grid lg:grid-cols-2"
    >
      <div className="flex flex-col items-center gap-xs px-2xl text-center">
        {/* The design breaks the question over two lines. A width does that
            without a hard break, which a translation would put in the wrong
            place; this one balances the two lines rather than leaving a widow. */}
        <h2 className="type-heading-xl max-w-56 text-foreground">
          {t('toolsHub.browseByCategory.notSureWhereToStart')}
        </h2>
        <p className="type-body-md text-foreground-subtle">
          {t('toolsHub.browseByCategory.browseOurCuratedList')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-grid">
        {CARD_ORDER.map((category) => {
          const config = LEARN_CARD_CONFIG[category];
          const label = categoryLabels?.[category]?.label ?? config.label;
          const subtitle = categoryLabels?.[category]?.subtitle ?? config.subtitle;
          const Icon = CATEGORY_ICONS[category];

          return (
            <div
              key={category}
              role="button"
              tabIndex={0}
              data-category={category}
              aria-label={label}
              onClick={() => onCategoryClick(category)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCategoryClick(category);
                }
              }}
              className="relative h-45 cursor-pointer overflow-hidden rounded-lg bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <img src={config.poster} alt="" className="block size-full object-cover" />
              {/* The photographs vary; the wash is what keeps white type legible
                  on all four of them. */}
              <div className="pointer-events-none absolute inset-0 flex flex-col justify-between bg-linear-to-b from-black/10 to-black/35 p-lg">
                <span className="flex size-10 items-center justify-center rounded-lg bg-white/20">
                  <Icon className="size-4.5 text-white" />
                </span>
                <div className="flex flex-col gap-3xs text-white">
                  <span className="type-heading-lg">{label}</span>
                  <span className="type-body-md text-white/90">{subtitle}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

BrowseByCategorySection.displayName = 'BrowseByCategorySection';
